import { defineStore } from 'pinia'
import http from '@/services/http'

/**
 * Com o token em cookie HttpOnly o SPA não consegue mais olhar se tem sessão: pergunta
 * à API. Guard de rota e boot disputam a resposta, então é memoizada.
 */
let sessionCheck = null

export const useAuthStore = defineStore('auth', {
    state: () => {
        return {
            isAuthenticated: false,
            // Segura a renderização até a primeira resposta da API, senão a tela pisca.
            isBootstrapping: true,
            verifiedEmail: '',
            isAdmin: false,
            twoFactorChallenge: '',
            twoFactorCodeExpiresAt: 0,
            recoverPassword: {
                email: '',
                attempts: 0,
                maxAttempts: 3
            }
        }
    },
    getters: {
        getAuthenticatedStatus: state => state.isAuthenticated,
    },
    actions: {
        setStatusAuthentication(status) {
            this.isAuthenticated = status
        },
        setIsAdmin(isAdmin) {
            this.isAdmin = Boolean(isAdmin)
        },
        setTwoFactorChallenge({ challenge, expire_time }) {
            this.twoFactorChallenge = challenge
            this.twoFactorCodeExpiresAt = challenge
                ? Date.now() + Number(expire_time) * 1000
                : 0
        },

        /** Não guarda token: ele veio no Set-Cookie e é inalcançável por JavaScript. */
        setSession(data) {
            this.setIsAdmin(data.user?.is_admin)
            this.setStatusAuthentication(true)
            this.setTwoFactorChallenge({ challenge: '', expire_time: 0 })
            this.isBootstrapping = false
            sessionCheck = Promise.resolve()
        },

        /**
         * Pergunta à API se existe sessão. notifySessionExpired: false porque 401 aqui
         * é visitante anônimo, não sessão expirada.
         */
        async ensureSession() {
            if (!sessionCheck) {
                sessionCheck = http
                    .get('/validate-token', { notifySessionExpired: false })
                    .then(() => {
                        this.setStatusAuthentication(true)
                    })
                    .catch(() => {
                        this.setStatusAuthentication(false)
                    })
                    .finally(() => {
                        this.isBootstrapping = false
                    })
            }

            await sessionCheck

            return this.isAuthenticated
        },

        /** Encerra no servidor: antes o token seguia válido na API até vencer. */
        async logout() {
            try {
                await http.post('/auth/logout', null, { skipAuthRefresh: true })
            } catch {
                // Falhar na rede não pode impedir a pessoa de sair no cliente.
            } finally {
                this.clearSession()
            }
        },

        /** Chamado também pelo cliente HTTP quando a renovação falha. */
        clearSession() {
            this.setStatusAuthentication(false)
            this.setIsAdmin(false)
            this.setTwoFactorChallenge({ challenge: '', expire_time: 0 })
            this.isBootstrapping = false
            sessionCheck = null
        },

        setVerifiedEmail(email) {
            this.recoverPassword.email = email
        }
    },
    // Só recuperação de senha. Antes `persist: true` serializava o state inteiro,
    // token incluído, criando uma segunda cópia da credencial.
    persist: {
        paths: ['recoverPassword']
    }
})
